<template>
  <div class="chunking-panel">
    <!-- Header -->
    <div class="panel-header">
      <div class="header-left">
        <i class="fas fa-layer-group"></i>
        <span>Document Chunking</span>
        <span :class="['tier-badge', `tier-badge--${tierKey}`]" v-if="status">
          {{ tierLabel }}
        </span>
      </div>
      <button class="close-btn" @click="$emit('close')" title="Close">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="panel-loading">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Loading chunk status...</span>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="panel-error">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ error }}</span>
      <button @click="loadStatus" class="retry-btn">Retry</button>
    </div>

    <!-- Content -->
    <div v-else-if="status" class="panel-body">

      <!-- Document Info Row -->
      <div class="info-row">
        <div class="info-item">
          <span class="info-label"><i class="fas fa-file-pdf"></i> Pages</span>
          <span class="info-value">{{ status.page_count || '—' }}</span>
        </div>
        <div class="info-item">
          <span class="info-label"><i class="fas fa-coins"></i> Est. Tokens</span>
          <span class="info-value">{{ formatNumber(status.estimated_tokens) }}</span>
        </div>
        <div class="info-item">
          <span class="info-label"><i class="fas fa-cubes"></i> Chunks</span>
          <span class="info-value">{{ status.chunk_count }}</span>
        </div>
        <div class="info-item">
          <span class="info-label"><i class="fas fa-circle"></i> Status</span>
          <span :class="['status-badge', `status-badge--${status.chunking_status}`]">
            {{ formatStatus(status.chunking_status) }}
          </span>
        </div>
      </div>

      <!-- Tier 1 notice -->
      <div v-if="status.processing_tier === 'tier1'" class="tier1-notice">
        <i class="fas fa-info-circle"></i>
        <div>
          <strong>No chunking needed.</strong>
          This document ({{ status.page_count }} pages) is small enough to be analyzed as a full document.
          Chunking is only used for documents over 20 pages.
        </div>
      </div>

      <!-- Error message -->
      <div v-if="status.chunking_error" class="error-notice">
        <i class="fas fa-exclamation-circle"></i>
        <div>
          <strong>Last error:</strong> {{ status.chunking_error }}
        </div>
      </div>

      <!-- Trigger / Action Buttons -->
      <div class="action-bar" v-if="status.processing_tier !== 'tier1'">
        <button
          v-if="status.chunking_status !== 'complete'"
          @click="triggerChunking"
          :disabled="isChunking"
          class="btn btn-primary"
        >
          <i :class="isChunking ? 'fas fa-spinner fa-spin' : 'fas fa-scissors'"></i>
          {{ isChunking ? 'Chunking...' : (status.chunking_status === 'failed' ? 'Retry Chunking' : 'Chunk Document') }}
        </button>

        <button
          v-if="status.chunking_status === 'complete'"
          @click="triggerChunking"
          :disabled="isChunking"
          class="btn btn-secondary"
        >
          <i class="fas fa-redo"></i>
          Re-chunk
        </button>

        <button @click="loadStatus" class="btn btn-ghost" :disabled="loading">
          <i class="fas fa-sync-alt"></i>
          Refresh
        </button>
      </div>

      <!-- Completed timestamp -->
      <div v-if="status.chunking_completed_at" class="completed-note">
        <i class="fas fa-check-circle"></i>
        Chunked {{ formatDate(status.chunking_completed_at) }}
      </div>

      <!-- Chunk List -->
      <div v-if="status.chunks && status.chunks.length > 0" class="chunks-section">
        <div class="chunks-header">
          <span class="chunks-title">
            <i class="fas fa-list"></i>
            {{ status.chunks.length }} Chunks
          </span>
          <span class="chunks-legend">
            <span class="legend-dot legend-dot--processed"></span> Analyzed
            <span class="legend-dot legend-dot--pending"></span> Pending
          </span>
        </div>
        <div class="chunks-grid">
          <div
            v-for="chunk in status.chunks"
            :key="chunk.chunk_index"
            :class="['chunk-card', chunk.is_processed ? 'chunk-card--processed' : 'chunk-card--pending']"
            :title="chunkTooltip(chunk)"
          >
            <div class="chunk-header">
              <span class="chunk-index">#{{ chunk.chunk_index + 1 }}</span>
              <span class="chunk-pages">
                <i class="fas fa-file-alt"></i>
                {{ formatPageRange(chunk.page_numbers) }}
              </span>
              <i
                :class="['chunk-status-icon', chunk.is_processed ? 'fas fa-check-circle processed' : 'far fa-clock pending']"
              ></i>
            </div>
            <div class="chunk-meta">
              <span class="chunk-chars">{{ formatNumber(chunk.char_count) }} chars</span>
              <span v-if="chunk.content_tags && chunk.content_tags.length" class="chunk-tags">
                <span v-for="tag in chunk.content_tags.slice(0, 2)" :key="tag" class="tag">{{ tag }}</span>
              </span>
            </div>
            <div v-if="chunk.summary" class="chunk-summary">{{ truncate(chunk.summary, 100) }}</div>
          </div>
        </div>
      </div>

      <!-- Empty state — no chunks yet -->
      <div v-else-if="status.processing_tier !== 'tier1' && status.chunking_status !== 'processing'" class="no-chunks">
        <i class="fas fa-layer-group"></i>
        <p>No chunks yet. Click <strong>Chunk Document</strong> to split this document for parallel analysis.</p>
      </div>

      <!-- Processing state -->
      <div v-else-if="status.chunking_status === 'processing'" class="chunking-progress">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Chunking in progress... This usually takes 1–2 minutes.</p>
        <button @click="startPolling" class="btn btn-ghost btn-sm">
          <i class="fas fa-sync-alt"></i> Auto-refresh
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'DocumentChunkingPanel',

  props: {
    documentId: {
      type: Number,
      required: true,
    },
  },

  emits: ['close', 'chunking-complete'],

  data() {
    return {
      status: null,
      loading: false,
      error: null,
      isChunking: false,
      pollInterval: null,
    };
  },

  computed: {
    tierKey() {
      return this.status?.processing_tier || 'tier1';
    },
    tierLabel() {
      const labels = {
        tier1: 'Tier 1 — Full Doc',
        tier2: 'Tier 2 — Chunked',
        tier3: 'Tier 3 — Hierarchical',
      };
      return labels[this.status?.processing_tier] || 'Unknown';
    },
  },

  mounted() {
    this.loadStatus();
  },

  beforeUnmount() {
    this.stopPolling();
  },

  methods: {
    async loadStatus() {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.get(`/documents/${this.documentId}/chunk-status/`);
        this.status = response.data;

        if (this.status.chunking_status === 'processing') {
          this.startPolling();
        } else {
          this.stopPolling();
          if (this.status.chunking_status === 'complete') {
            this.$emit('chunking-complete', this.status);
          }
        }
      } catch (err) {
        this.error = err.response?.data?.error || 'Failed to load chunk status.';
      } finally {
        this.loading = false;
      }
    },

    async triggerChunking() {
      this.isChunking = true;
      this.error = null;
      try {
        await api.post(`/documents/${this.documentId}/chunk/`);
        await this.loadStatus();
        this.startPolling();
      } catch (err) {
        this.error = err.response?.data?.error || 'Failed to start chunking.';
        this.isChunking = false;
      }
    },

    startPolling() {
      this.stopPolling();
      this.pollInterval = setInterval(async () => {
        await this.loadStatus();
        if (this.status?.chunking_status !== 'processing') {
          this.stopPolling();
          this.isChunking = false;
        }
      }, 5000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    formatStatus(s) {
      return { pending: 'Pending', processing: 'Processing…', complete: 'Complete', failed: 'Failed' }[s] || s || 'Pending';
    },

    formatPageRange(pages) {
      if (!pages || pages.length === 0) return '—';
      if (pages.length === 1) return `p. ${pages[0]}`;
      return `pp. ${pages[0]}–${pages[pages.length - 1]}`;
    },

    formatNumber(n) {
      if (!n) return '—';
      return n.toLocaleString();
    },

    formatDate(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString();
    },

    truncate(text, max) {
      if (!text) return '';
      return text.length > max ? text.slice(0, max) + '…' : text;
    },

    chunkTooltip(chunk) {
      return [
        `Chunk ${chunk.chunk_index + 1}`,
        `Pages: ${this.formatPageRange(chunk.page_numbers)}`,
        `${this.formatNumber(chunk.char_count)} characters`,
        chunk.is_processed ? 'Analyzed ✓' : 'Not yet analyzed',
      ].join('\n');
    },
  },
};
</script>

<style scoped>
.chunking-panel {
  background: var(--surface-color, #1e2235);
  border: 1px solid var(--border-color, #2d3250);
  border-radius: 12px;
  overflow: hidden;
  font-size: 0.875rem;
}

/* Header */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: var(--surface-elevated, #252a3d);
  border-bottom: 1px solid var(--border-color, #2d3250);
}
.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: var(--text-primary, #e2e8f0);
}
.header-left i { color: var(--primary-color, #6366f1); }
.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted, #94a3b8);
  padding: 4px 6px;
  border-radius: 6px;
  transition: all 0.15s;
}
.close-btn:hover { background: rgba(255,255,255,0.08); color: var(--text-primary, #e2e8f0); }

/* Tier badge */
.tier-badge {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.tier-badge--tier1 { background: rgba(100, 116, 139, 0.2); color: #94a3b8; }
.tier-badge--tier2 { background: rgba(99, 102, 241, 0.15); color: #818cf8; }
.tier-badge--tier3 { background: rgba(245, 158, 11, 0.15); color: #fbbf24; }

/* Loading / Error */
.panel-loading, .panel-error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px 18px;
  color: var(--text-muted, #94a3b8);
}
.panel-error { color: #f87171; }
.retry-btn {
  margin-left: 8px;
  background: none;
  border: 1px solid #f87171;
  color: #f87171;
  border-radius: 6px;
  padding: 2px 10px;
  cursor: pointer;
  font-size: 0.8rem;
}

/* Body */
.panel-body { padding: 16px 18px; display: flex; flex-direction: column; gap: 14px; }

/* Info row */
.info-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.info-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: var(--surface-elevated, #252a3d);
  border-radius: 8px;
  padding: 8px 12px;
  flex: 1;
  min-width: 90px;
}
.info-label { font-size: 0.72rem; color: var(--text-muted, #94a3b8); display: flex; align-items: center; gap: 5px; }
.info-value { font-size: 1rem; font-weight: 600; color: var(--text-primary, #e2e8f0); }

/* Status badge */
.status-badge {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  width: fit-content;
}
.status-badge--pending   { background: rgba(100,116,139,0.2); color: #94a3b8; }
.status-badge--processing { background: rgba(99,102,241,0.15); color: #818cf8; }
.status-badge--complete  { background: rgba(34,197,94,0.15);  color: #4ade80; }
.status-badge--failed    { background: rgba(248,113,113,0.15); color: #f87171; }

/* Notices */
.tier1-notice, .error-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.82rem;
  line-height: 1.5;
}
.tier1-notice {
  background: rgba(100,116,139,0.12);
  color: #94a3b8;
  border: 1px solid rgba(100,116,139,0.2);
}
.tier1-notice i { color: #64748b; margin-top: 2px; }
.error-notice {
  background: rgba(248,113,113,0.08);
  color: #f87171;
  border: 1px solid rgba(248,113,113,0.2);
}

/* Action bar */
.action-bar { display: flex; gap: 10px; flex-wrap: wrap; }

.btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary  { background: var(--primary-color, #6366f1); color: white; }
.btn-primary:not(:disabled):hover { background: #4f52d8; }
.btn-secondary { background: rgba(99,102,241,0.15); color: #818cf8; border: 1px solid rgba(99,102,241,0.3); }
.btn-secondary:not(:disabled):hover { background: rgba(99,102,241,0.25); }
.btn-ghost  { background: none; color: var(--text-muted, #94a3b8); border: 1px solid var(--border-color, #2d3250); }
.btn-ghost:not(:disabled):hover { background: rgba(255,255,255,0.05); color: var(--text-primary, #e2e8f0); }
.btn-sm { padding: 5px 12px; font-size: 0.8rem; }

/* Completed note */
.completed-note {
  font-size: 0.78rem;
  color: #4ade80;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Chunks section */
.chunks-section { display: flex; flex-direction: column; gap: 10px; }
.chunks-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.chunks-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-muted, #94a3b8);
  display: flex;
  align-items: center;
  gap: 6px;
}
.chunks-legend {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.75rem;
  color: var(--text-muted, #94a3b8);
}
.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}
.legend-dot--processed { background: #4ade80; }
.legend-dot--pending   { background: #94a3b8; }

.chunks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
}

.chunk-card {
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid var(--border-color, #2d3250);
  background: var(--surface-elevated, #252a3d);
  cursor: default;
  transition: border-color 0.15s;
}
.chunk-card--processed { border-color: rgba(74,222,128,0.25); }
.chunk-card--pending   { border-color: var(--border-color, #2d3250); }
.chunk-card:hover { border-color: rgba(99,102,241,0.4); }

.chunk-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 5px;
}
.chunk-index {
  font-weight: 700;
  font-size: 0.78rem;
  color: var(--text-primary, #e2e8f0);
}
.chunk-pages {
  flex: 1;
  font-size: 0.75rem;
  color: var(--text-muted, #94a3b8);
  display: flex;
  align-items: center;
  gap: 4px;
}
.chunk-status-icon.processed { color: #4ade80; font-size: 0.78rem; }
.chunk-status-icon.pending   { color: #64748b; font-size: 0.78rem; }

.chunk-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.chunk-chars { font-size: 0.7rem; color: #64748b; }
.chunk-tags { display: flex; gap: 4px; flex-wrap: wrap; }
.tag {
  font-size: 0.65rem;
  background: rgba(99,102,241,0.12);
  color: #818cf8;
  border-radius: 4px;
  padding: 1px 5px;
  text-transform: capitalize;
}

.chunk-summary {
  margin-top: 5px;
  font-size: 0.72rem;
  color: #64748b;
  line-height: 1.4;
}

/* Empty / progress states */
.no-chunks, .chunking-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  text-align: center;
  color: var(--text-muted, #94a3b8);
}
.no-chunks i, .chunking-progress i {
  font-size: 2rem;
  color: var(--border-color, #2d3250);
}
.no-chunks p, .chunking-progress p { font-size: 0.85rem; line-height: 1.5; }
</style>
